<script>
//import VTooltip from 'v-tooltip';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import { mapGetters } from 'vuex';

import { computedEmitterFactory } from '../../../util/util';

export default {
    name: 'Configuration',

    props: {
        modemConfig: Object,
        mainConfig: Object,
        modemConfigEdits: Object,
        mainConfigEdits: Object,
        edits: Boolean,
        revertAll: Function,
        sendCommand: Function,
    },

    data() {
        return {
            zwaveOptions: [
                {
                    label: this.$t('normal'),
                    value: 'ZWAVE_MODE_NORMAL',
                },
                {
                    label: this.$t('inclusion'),
                    value: 'ZWAVE_MODE_INCLUSION',
                },
                {
                    label: this.$t('exclusion'),
                    value: 'ZWAVE_MODE_EXCLUSION',
                },
            ],
        };
    },

    computed: {
        ...computedEmitterFactory(
            [
                'tstar_server_url', // tstarServerUrl
            ],
            'modemConfigEdits',
        ),

        ...computedEmitterFactory(
            [
                'arming_countdown_sec', // armingCountdownSec
                'zwave_mode', // zwaveMode
            ],
            'mainConfigEdits',
        ),

        ...mapGetters(['isUserAdmin']),
    },

    methods: {
        revertArmCountdown() {
            this.armingCountdownSec = this.mainConfig.arming_countdown_sec;
        },

        revertServerUrl() {
            this.tstarServerUrl = this.modemConfig.tstar_server_url;
        },

        revertZwaveMode() {
            this.zwaveMode = this.mainConfig.zwave_mode;
        },

        setZwaveMode(value) {
            this.zwaveMode = value;
        },
    },
};
</script>

<template>
    <div class="unit-details-card" :class="{ 'non-admin': !isUserAdmin }">
        <div class="prop-block">
            <div class="prop name">{{ $t('serverAddress') }}</div>
            <div class="prop">
                <div class="input-box" v-if="isUserAdmin">
                    <InputBox type="text" center v-model="tstarServerUrl">
                        <div
                            class="current-label center-flex"
                            :class="{
                                discrepancy:
                                    tstarServerUrl !==
                                    modemConfig.tstar_server_url,
                            }"
                        >
                            {{ $t('currently') }}:
                            {{ modemConfig.tstar_server_url }}
                            <button
                                tabindex="-1"
                                class="revert-icon"
                                @click="revertServerUrl"
                                v-tooltip.right="$t('revertValue')"
                            >
                                <i class="fa fa-undo-alt"></i>
                            </button>
                        </div>
                    </InputBox>
                </div>
            </div>
        </div>
        <div class="prop-block">
            <div class="prop name">{{ $t('armCountdown') }}</div>
            <div class="prop">
                <div class="input-box" v-if="isUserAdmin">
                    <InputBox type="number" center v-model="armingCountdownSec">
                        <div
                            class="current-label center-flex"
                            :class="{
                                discrepancy:
                                    armingCountdownSec !==
                                    mainConfig.arming_countdown_sec,
                            }"
                        >
                            {{ $t('currently') }}:
                            {{ mainConfig.arming_countdown_sec }}
                            <button
                                tabindex="-1"
                                class="revert-icon"
                                @click="revertArmCountdown"
                                v-tooltip.right="$t('revertValue')"
                            >
                                <i class="fa fa-undo-alt"></i>
                            </button>
                        </div>
                    </InputBox>
                </div>
            </div>
        </div>
        <div class="prop-block">
            <div class="prop name">{{ $t('zwaveMode') }}</div>
            <div class="prop">
                <div class="input-box" v-if="isUserAdmin">
                    <DropdownMenu
                        :options="zwaveOptions"
                        :value="zwaveMode"
                        outline
                        @change="setZwaveMode"
                    >
                        <div
                            class="current-label center-flex"
                            :class="{
                                discrepancy:
                                    armingCountdownSec !==
                                    mainConfig.zwave_mode,
                            }"
                        >
                            {{ $t('currently') }}: {{ mainConfig.zwave_mode }}
                            <button
                                tabindex="-1"
                                class="revert-icon"
                                @click="revertZwaveMode"
                                v-tooltip.right="$t('revertValue')"
                            >
                                <i class="fa fa-undo-alt"></i>
                            </button>
                        </div>
                    </DropdownMenu>
                </div>
            </div>
        </div>
        <div class="button-row">
            <Button outline @click="revertAll" :disabled="!edits">
                {{ $t('cancel') }}
            </Button>
            <Button @click="sendCommand" :disabled="!edits">
                {{ $t('confirm') }}
            </Button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.unit-details-container {
    .unit-details-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        color: $theme-dark-grey;

        &.non-admin {
            .prop-block {
                align-items: center;

                .prop {
                    display: flex;
                    height: 100%;
                    align-items: center;
                }
            }
        }

        .prop-block {
            display: flex;
            flex-flow: column;
            justify-content: space-between;
            align-items: baseline;
            width: 100%;
            padding: 0 8px 22px;

            .prop {
                width: 100%;
                position: relative;

                .value {
                    display: flex;
                    width: 100%;
                    align-items: center;
                    justify-content: center;
                }

                .input-box {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-grow: 1;
                    margin-bottom: 10px;
                    position: relative;

                    .border-line {
                        border-bottom: 2px solid $theme-light-grey;
                        padding-bottom: 12px;
                        margin-right: 35px;
                        width: 175px;
                        position: relative;
                    }

                    .input-box-container {
                        min-width: 175px;
                        max-width: 250px;
                        flex-grow: 1;
                        position: relative;
                    }

                    .dropdown-container {
                        min-width: 140px;
                    }
                }

                .current-label {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    background-color: color.adjust(
                        $theme-light-grey,
                        $lightness: -5%
                    );
                    padding: 3px 4px 3px 2px;
                    color: white;
                    transform: scaleY(0);
                    transition: transform 0.5s;
                    font-size: 0.8em;
                    transform-origin: top;
                    white-space: nowrap;

                    &:before {
                        content: '';
                        height: 100%;
                        position: absolute;
                        right: 100%;
                        top: 0;
                        background-image: url('../../../../assets/CurrentValueSlash.svg');
                        background-size: 100%;
                        width: 8px;
                    }

                    &.discrepancy {
                        transform: scaleY(1);
                    }

                    .revert-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 19px;
                        height: 19px;
                        margin: 0;
                        padding: 2px;
                        margin-left: 3px;
                        transition: transform 0.25s;
                        color: color.adjust(white, $lightness: -10%);
                        font-size: 0.9em;
                        padding: 1px 0 0 0;
                        background-color: transparent;
                        border: none;

                        &:focus {
                            outline: none;
                        }

                        &:active {
                            font-size: 0.8em;
                        }

                        &:hover {
                            transform: rotate(-20deg);
                        }
                    }

                    &:hover {
                        cursor: default;

                        .revert-icon {
                            color: white;

                            &:hover {
                                color: $theme-light-blue;
                                cursor: pointer;
                            }
                        }
                    }
                }

                &.name {
                    font-weight: bold;
                }

                .button-box {
                    display: flex;
                    flex-flow: row nowrap;
                    min-width: 35px;

                    button {
                        min-width: 35px;
                        margin-left: 5px;
                    }
                }
            }
        }

        .button-row {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;

            & > .button {
                margin: 0 15px;
            }
        }
    }
}

@media (min-width: 600px) {
    .unit-details-container .unit-details-card {
        .prop-block {
            flex-flow: row nowrap;

            .prop {
                width: 47%;
            }
        }
    }
}
</style>
